import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MockBadge, Spinner, Button, Stat } from "../components/ui";
import {
  Users as UsersIcon, Trash2, Shield, ShieldOff, Power, DollarSign, Activity,
  CheckCircle2, AlertCircle, Clock, ScrollText, Send, Database, LayoutDashboard,
  SlidersHorizontal, Eye, EyeOff, Building2, KeyRound, Home, Map, Globe2,
  Calculator, ReceiptText, Crosshair, Hammer, Bell, Sun, Bookmark, BarChart2,
  Settings as SettingsIcon, Code2, Zap, History,
} from "lucide-react";
import { PipelineFunnel, RunHistory } from "../components/PipelineInsights";
import { useI18n } from "../i18n/I18nContext";

const TABS = [
  { key: "dashboard", icon: LayoutDashboard },
  { key: "users", icon: UsersIcon },
  { key: "features", icon: SlidersHorizontal },
  { key: "costs", icon: DollarSign },
  { key: "jobs", icon: Activity },
  { key: "audit", icon: ScrollText },
  { key: "telegram", icon: Send },
  { key: "db", icon: Database },
];

const JOB_LABEL_KEY = { pipeline: "jobPipeline", daily_brief: "jobDaily_brief", note_cleanup: "jobNote_cleanup" };

// Same key -> {icon, i18n label key} mapping as AppShell.jsx's nav_items, for
// the subset that's admin-toggleable (routers/admin.py TOGGLEABLE_FEATURES).
const FEATURE_META = {
  listings: { icon: Building2, labelKey: "nav.listings" },
  mieten: { icon: KeyRound, labelKey: "nav.mieten" },
  personalSearch: { icon: Home, labelKey: "nav.personalSearch" },
  markets: { icon: Map, labelKey: "nav.markets" },
  map: { icon: Globe2, labelKey: "nav.map" },
  underwriter: { icon: Calculator, labelKey: "nav.underwriter" },
  rentcalc: { icon: ReceiptText, labelKey: "nav.rentCalculator" },
  bewerter: { icon: Crosshair, labelKey: "nav.bewerter" },
  quickUnderwrite: { icon: Zap, labelKey: "nav.quickUnderwrite" },
  fixflip: { icon: Hammer, labelKey: "nav.fixflip" },
  alerts: { icon: Bell, labelKey: "nav.alerts" },
  briefs: { icon: Sun, labelKey: "nav.briefs" },
  watchlist: { icon: Bookmark, labelKey: "nav.watchlist" },
  archive: { icon: History, labelKey: "nav.archive" },
  analysis: { icon: BarChart2, labelKey: "nav.analysis" },
  settings: { icon: SettingsIcon, labelKey: "nav.settings" },
  api: { icon: Code2, labelKey: "nav.api" },
};

export default function Admin() {
  const { t } = useI18n();
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("admin.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("admin.title")}</h1>
          <p className="mt-2 text-xs text-text-muted max-w-xl">{t("admin.subtitle")}</p>
        </div>
        <MockBadge />
      </div>

      <div className="px-6 md:px-10 pt-5 flex items-center gap-1 border-b border-border">
        {TABS.map(({ key, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest2 border-b-2 -mb-px transition-colors ${
              tab === key
                ? "border-signal-green text-white"
                : "border-transparent text-text-muted hover:text-white"
            }`}
          >
            <Icon size={13} /> {t(`admin.tab${key[0].toUpperCase()}${key.slice(1)}`)}
          </button>
        ))}
      </div>

      <div className="p-6 md:p-10">
        {tab === "dashboard" && <DashboardTab />}
        {tab === "users" && <UsersTab />}
        {tab === "features" && <FeaturesTab />}
        {tab === "costs" && <CostsTab />}
        {tab === "jobs" && <JobsTab />}
        {tab === "audit" && <AuditLogTab />}
        {tab === "telegram" && <TelegramTab />}
        {tab === "db" && <DbStatsTab />}
      </div>
    </div>
  );
}

/**
 * Operator view of the pipeline: the 5-stage funnel and "why did the count
 * move?". Both used to sit on the investor Dashboard, where they answered a
 * question no investor asks.
 */
function DashboardTab() {
  const { t } = useI18n();
  const [funnel, setFunnel] = useState({ funnel: [], available: false });
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [f, r] = await Promise.all([
          api.get("/funnel"),
          api.get("/pipeline/runs", { params: { limit: 20 } }),
        ]);
        setFunnel(f.data);
        setRuns(r.data.runs || []);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-10"><Spinner /></div>;
  if (err) return <div className="text-xs font-mono text-signal-red">{err}</div>;

  return (
    <div className="space-y-8">
      <PipelineFunnel funnel={funnel} t={t} />
      <section>
        <div className="overline">{t("dashboard.runHistory")}</div>
        <h3 className="mt-1 font-heading text-2xl font-bold">{t("dashboard.whyCountMoved")}</h3>
        <RunHistory runs={runs} t={t} />
      </section>
    </div>
  );
}

function UsersTab() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) {
      console.error("Admin users load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/users/${id}`, body);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(t("admin.confirmDelete", { email: u.email }))) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!users && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  return (
    <div className="border border-border">
      <div className="overline px-5 py-3 border-b border-border">{t("admin.usersTitle")}</div>
      {users.length === 0 ? (
        <div className="p-8 text-center text-xs font-mono text-text-muted">{t("admin.noUsers")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left text-text-muted uppercase tracking-widest2 text-[10px] border-b border-border">
                <th className="px-5 py-3 font-normal">{t("admin.colEmail")}</th>
                <th className="px-3 py-3 font-normal">{t("admin.colName")}</th>
                <th className="px-3 py-3 font-normal">{t("admin.colRole")}</th>
                <th className="px-3 py-3 font-normal">{t("admin.colStatus")}</th>
                <th className="px-3 py-3 font-normal">TELEGRAM</th>
                <th className="px-3 py-3 font-normal">{t("admin.colCreated")}</th>
                <th className="px-3 py-3 font-normal">{t("admin.colLastLogin")}</th>
                <th className="px-5 py-3 font-normal text-right">{t("admin.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                const busy = busyId === u.id;
                return (
                  <tr key={u.id} className="border-b border-border last:border-b-0 hover:bg-surfaceHover">
                    <td className="px-5 py-3 text-white">
                      {u.email} {isSelf && <span className="text-text-muted">({t("admin.you")})</span>}
                    </td>
                    <td className="px-3 py-3 text-text-secondary">{u.name || "—"}</td>
                    <td className="px-3 py-3">
                      <span className={u.role === "admin" ? "text-signal-green" : "text-text-secondary"}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={u.disabled ? "text-signal-red" : "text-signal-green"}>
                        {u.disabled ? t("admin.statusDisabled") : t("admin.statusActive")}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {u.telegram_linked
                        ? <span title="Telegram bot linked" className="text-signal-green">✓</span>
                        : <span className="text-text-muted">—</span>}
                    </td>
                    <td className="px-3 py-3 text-text-muted">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-3 text-text-muted">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : t("admin.never")}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          className="!px-2 !py-1 !text-[10px]"
                          disabled={isSelf || busy}
                          onClick={() => patch(u.id, { role: u.role === "admin" ? "user" : "admin" })}
                          title={u.role === "admin" ? t("admin.makeUser") : t("admin.makeAdmin")}
                        >
                          {u.role === "admin" ? <ShieldOff size={12} /> : <Shield size={12} />}
                        </Button>
                        <Button
                          variant="ghost"
                          className="!px-2 !py-1 !text-[10px]"
                          disabled={isSelf || busy}
                          onClick={() => patch(u.id, { disabled: !u.disabled })}
                          title={u.disabled ? t("admin.enable") : t("admin.disable")}
                        >
                          <Power size={12} />
                        </Button>
                        <Button
                          variant="danger"
                          className="!px-2 !py-1 !text-[10px]"
                          disabled={isSelf || busy}
                          onClick={() => remove(u)}
                          title={t("admin.delete")}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CostsTab() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/costs");
      setData(data);
    } catch (e) {
      console.error("Admin costs load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  const fmt = (n) => `$${n.toFixed(4)}`;

  return (
    <div>
      <div className="overline mb-3">{t("admin.costsTitle")}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
        <Stat label={t("admin.totalSpend")} value={fmt(data.total_cost_usd)} accent="green" />
        <Stat label={t("admin.thisMonth")} value={fmt(data.month_cost_usd)} />
        <Stat label={t("admin.totalRequests")} value={data.total_requests.toLocaleString()} />
        <Stat
          label={t("admin.totalTokens")}
          value={`${data.total_input_tokens.toLocaleString()} / ${data.total_output_tokens.toLocaleString()}`}
        />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border">
          <div className="overline px-5 py-3 border-b border-border">{t("admin.byDay")}</div>
          {data.by_day.length === 0 ? (
            <div className="p-6 text-center text-xs font-mono text-text-muted">{t("admin.noCostData")}</div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-left text-text-muted uppercase tracking-widest2 text-[10px] border-b border-border">
                    <th className="px-5 py-2 font-normal">{t("admin.colDate")}</th>
                    <th className="px-5 py-2 font-normal text-right">{t("admin.colCost")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.by_day].reverse().map((d) => (
                    <tr key={d.date} className="border-b border-border last:border-b-0">
                      <td className="px-5 py-2 text-text-secondary">{d.date}</td>
                      <td className="px-5 py-2 text-right text-white">{fmt(d.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border border-border">
          <div className="overline px-5 py-3 border-b border-border">{t("admin.byUser")}</div>
          {data.by_user.length === 0 ? (
            <div className="p-6 text-center text-xs font-mono text-text-muted">{t("admin.noCostData")}</div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-left text-text-muted uppercase tracking-widest2 text-[10px] border-b border-border">
                    <th className="px-5 py-2 font-normal">{t("admin.colEmail")}</th>
                    <th className="px-5 py-2 font-normal text-right">{t("admin.colRequests")}</th>
                    <th className="px-5 py-2 font-normal text-right">{t("admin.colCost")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_user.map((u) => (
                    <tr key={u.user_id} className="border-b border-border last:border-b-0">
                      <td className="px-5 py-2 text-text-secondary">{u.email}</td>
                      <td className="px-5 py-2 text-right text-text-muted">{u.requests}</td>
                      <td className="px-5 py-2 text-right text-white">{fmt(u.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function JobsTab() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/jobs");
      setData(data);
    } catch (e) {
      console.error("Admin jobs load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  return (
    <div>
      <div className="overline mb-3">{t("admin.jobsTitle")}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {data.jobs.map((j) => <JobCard key={j.job_id} job={j} t={t} />)}
      </div>
    </div>
  );
}

function LoadingOrError({ loading, err, onRetry, t }) {
  if (loading) return <div className="p-10"><Spinner /></div>;
  return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={onRetry} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );
}

function AuditLogTab() {
  const { t } = useI18n();
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/audit-log");
      setEntries(data.entries);
    } catch (e) {
      console.error("Admin audit-log load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!entries && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return <LoadingOrError err={err} onRetry={load} t={t} />;

  return (
    <div className="border border-border">
      <div className="overline px-5 py-3 border-b border-border">{t("admin.auditTitle")}</div>
      {entries.length === 0 ? (
        <div className="p-8 text-center text-xs font-mono text-text-muted">{t("admin.noAuditEntries")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left text-text-muted uppercase tracking-widest2 text-[10px] border-b border-border">
                <th className="px-5 py-2 font-normal">{t("admin.colWhen")}</th>
                <th className="px-3 py-2 font-normal">{t("admin.colActor")}</th>
                <th className="px-3 py-2 font-normal">{t("admin.colAction")}</th>
                <th className="px-3 py-2 font-normal">{t("admin.colTarget")}</th>
                <th className="px-5 py-2 font-normal">{t("admin.colDetail")}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-b-0">
                  <td className="px-5 py-2 text-text-muted whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 text-text-secondary">{e.actor_email}</td>
                  <td className="px-3 py-2 text-signal-green">{e.action}</td>
                  <td className="px-3 py-2 text-text-secondary">{e.target || "—"}</td>
                  <td className="px-5 py-2 text-text-muted truncate max-w-xs">{e.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TelegramTab() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/telegram-health");
      setData(data);
    } catch (e) {
      console.error("Admin telegram-health load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return <LoadingOrError err={err} onRetry={load} t={t} />;

  return (
    <div>
      <div className="overline mb-3">{t("admin.telegramTitle")}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border mb-8">
        <Stat label={t("admin.linkedChats")} value={data.bindings_count} accent="green" />
      </div>

      <div className="border border-border">
        <div className="overline px-5 py-3 border-b border-border">{t("admin.recentFailures")}</div>
        {data.recent_failures.length === 0 ? (
          <div className="p-8 text-center text-xs font-mono text-text-muted">{t("admin.noTelegramFailures")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-left text-text-muted uppercase tracking-widest2 text-[10px] border-b border-border">
                  <th className="px-5 py-2 font-normal">{t("admin.colWhen")}</th>
                  <th className="px-3 py-2 font-normal">{t("admin.colJob")}</th>
                  <th className="px-5 py-2 font-normal">{t("admin.colDetail")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_failures.map((f, i) => (
                  <tr key={i} className="border-b border-border last:border-b-0">
                    <td className="px-5 py-2 text-text-muted whitespace-nowrap">{f.at ? new Date(f.at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-signal-red">{f.job_id}</td>
                    <td className="px-5 py-2 text-text-secondary">{f.detail} {f.error ? `— ${f.error}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DbStatsTab() {
  const { t } = useI18n();
  const [tables, setTables] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/db-stats");
      setTables(data.tables);
    } catch (e) {
      console.error("Admin db-stats load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tables && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return <LoadingOrError err={err} onRetry={load} t={t} />;

  const rows = Object.entries(tables).sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <div className="overline mb-1">{t("admin.dbStatsTitle")}</div>
      <p className="text-xs text-text-muted mb-4 max-w-lg">{t("admin.dbStatsNote")}</p>
      <div className="border border-border">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-left text-text-muted uppercase tracking-widest2 text-[10px] border-b border-border">
              <th className="px-5 py-2 font-normal">{t("admin.colTable")}</th>
              <th className="px-5 py-2 font-normal text-right">{t("admin.colRows")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, count]) => (
              <tr key={name} className="border-b border-border last:border-b-0">
                <td className="px-5 py-2 text-text-secondary">{name}</td>
                <td className="px-5 py-2 text-right text-white">{count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobCard({ job, t }) {
  const label = t(`admin.${JOB_LABEL_KEY[job.job_id] || job.job_id}`);
  const never = !job.last_ran_at;
  const errored = job.last_status === "error";
  const statusIcon = never ? Clock : errored ? AlertCircle : job.stale ? AlertCircle : CheckCircle2;
  const StatusIcon = statusIcon;
  const statusClass = never ? "text-text-muted" : errored ? "text-signal-red" : job.stale ? "text-signal-yellow" : "text-signal-green";
  const statusLabel = never ? t("admin.jobStatusNever") : errored ? t("admin.jobStatusError") : job.stale ? t("admin.jobStatusStale") : t("admin.jobStatusOk");

  return (
    <div className="border border-border">
      <div className="p-5 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="overline">{label}</div>
            <div className={`mt-2 flex items-center gap-2 font-mono text-sm ${statusClass}`}>
              <StatusIcon size={15} /> {statusLabel}
            </div>
          </div>
        </div>
        <div className="mt-3 text-[11px] font-mono text-text-muted">
          {t("admin.jobLastRun")}: {never ? t("admin.jobNeverRun") : t("admin.jobHoursAgo", { hours: job.age_hours })}
        </div>
        <div className="mt-1 text-[11px] font-mono text-text-muted">
          {t("admin.jobExpectedEvery", { hours: job.expected_every_hours })}
        </div>
      </div>
      <div className="p-4">
        <div className="overline mb-2">{t("admin.jobHistory")}</div>
        {job.history.length === 0 ? (
          <div className="text-[11px] font-mono text-text-muted">{t("admin.jobNoHistory")}</div>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {job.history.map((h, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px] font-mono">
                <span className={h.status === "error" ? "text-signal-red" : "text-signal-green"}>●</span>
                <span className="text-text-muted flex-shrink-0">{h.at ? new Date(h.at).toLocaleString() : "—"}</span>
                {h.detail && <span className="text-text-secondary truncate">{h.detail}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturesTab() {
  const { t } = useI18n();
  const [available, setAvailable] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [err, setErr] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/features");
      setAvailable(data.available);
      setHidden(data.hidden_nav_items);
    } catch (e) {
      console.error("Admin features load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (key) => {
    const next = hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
    setBusyKey(key);
    try {
      const { data } = await api.post("/admin/features", { hidden_nav_items: next });
      setHidden(data.hidden_nav_items);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  if (!available && !err) return <div className="p-10"><Spinner /></div>;
  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  return (
    <div className="border border-border">
      <div className="overline px-5 py-3 border-b border-border">{t("admin.featuresTitle")}</div>
      <p className="px-5 py-3 text-xs text-text-muted border-b border-border">{t("admin.featuresSubtitle")}</p>
      <div>
        {available.map((key) => {
          const { icon: Icon, labelKey } = FEATURE_META[key] || {};
          const isHidden = hidden.includes(key);
          const busy = busyKey === key;
          return (
            <div
              key={key}
              className="flex items-center justify-between px-5 py-3 border-b border-border last:border-b-0"
            >
              <div className="flex items-center gap-3 text-xs font-mono text-white">
                {Icon && <Icon size={14} className="text-text-muted" />}
                <span>{t(labelKey)}</span>
              </div>
              <Button
                variant="ghost"
                className="!px-2 !py-1 !text-[10px]"
                disabled={busy}
                onClick={() => toggle(key)}
                data-testid={`features-toggle-${key}`}
              >
                {isHidden
                  ? <><EyeOff size={12} /> {t("admin.featureHidden")}</>
                  : <><Eye size={12} /> {t("admin.featureVisible")}</>}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
